import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CarFront } from "lucide-react";
import { useAuth } from "../lib/auth";
import { apiErrorMessage } from "../lib/api";

export default function Register() {
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(email, password, name, companyName);
      toast.success(`${companyName} paneli hazır — hoş geldiniz!`);
      nav("/ozet");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Kayıt başarısız"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-stone-50">
      <div className="hidden md:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(at 30% 20%, rgba(194,65,12,0.6) 0px, transparent 40%), radial-gradient(at 70% 90%, rgba(30,41,59,0.9) 0px, transparent 40%)",
          }}
        />
        <div className="relative flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-orange-600 flex items-center justify-center">
            <CarFront className="w-5 h-5" />
          </div>
          <div className="font-display font-black text-lg">Oto Galeri</div>
        </div>
        <div className="relative">
          <div className="text-xs tracking-[0.3em] uppercase text-orange-300 mb-4">
            Yönetim Paneli
          </div>
          <h1 className="font-display font-black text-4xl lg:text-5xl leading-tight">
            Galerini kur,
            <br />
            <span className="text-orange-400">yönetmeye başla.</span>
          </h1>
          <p className="mt-6 text-stone-300 max-w-md">
            Galeri bilgilerini gir, panelin anında hazır olsun. Stok, satış,
            kiralama ve giderleri tek yerden yönet.
          </p>
        </div>
        <div className="relative text-xs text-stone-400">
          © {new Date().getFullYear()} Oto Galeri & Kiralama Paneli
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-white rounded-2xl border border-stone-200 p-8 md:p-10"
        >
          <h2 className="font-display font-black text-3xl text-slate-900">
            Galeri kaydı
          </h2>
          <p className="text-stone-500 mt-2 text-sm">
            Yeni bir galeri hesabı oluşturun.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                Galeri Adı
              </label>
              <input
                required
                data-testid="register-company"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-stone-300 bg-white focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/20"
                placeholder="Örn. Yıldız Oto Galeri"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                Ad Soyad
              </label>
              <input
                required
                data-testid="register-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-stone-300 bg-white focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/20"
                placeholder="Ali Yılmaz"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                E-posta
              </label>
              <input
                type="email"
                required
                data-testid="register-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-stone-300 bg-white focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/20"
                placeholder="ornek@galeri.com"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                Şifre
              </label>
              <input
                type="password"
                required
                minLength={6}
                data-testid="register-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-stone-300 bg-white focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/20"
                placeholder="En az 6 karakter"
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="register-submit"
            disabled={busy}
            className="mt-6 w-full h-11 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold transition-colors disabled:opacity-60"
          >
            {busy ? "Kaydediliyor…" : "Galeriyi Oluştur"}
          </button>

          <p className="mt-6 text-sm text-stone-500 text-center">
            Zaten galerinizin hesabı var mı?{" "}
            <Link
              to="/giris"
              data-testid="goto-login"
              className="text-orange-700 font-semibold hover:underline"
            >
              Giriş yap
            </Link>
          </p>
          <p className="mt-3 text-xs text-stone-400 text-center">
            Diğer çalışanlar için hesabı yönetici, panelin
            <br />
            <b>Üyeler</b> bölümünden e-posta ve şifreyle oluşturur.
          </p>
          <div className="mt-6 pt-4 border-t border-stone-100 flex flex-wrap gap-x-3 gap-y-1 justify-center text-[11px] text-stone-500">
            <Link to="/mesafeli-satis-sozlesmesi" className="hover:text-orange-700">Mesafeli Satış Sözleşmesi</Link>
            <span>·</span>
            <Link to="/iptal-iade" className="hover:text-orange-700">İptal & İade</Link>
            <span>·</span>
            <Link to="/gizlilik" className="hover:text-orange-700">Gizlilik</Link>
            <span>·</span>
            <Link to="/kullanici-sozlesmesi" className="hover:text-orange-700">Kullanıcı Sözleşmesi</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
